import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DotsThreeIcon, DownloadSimpleIcon, SphereIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import $api from "@/zapi-frontend/shared/api/axios";
import { downloadDocument, type TVaultFile } from "@/zapi-frontend/shared/lib/downloadDocument";
import { useElementWidth } from "@/zapi-frontend/shared/hooks/useElementWidth";
import type { TLedgerFigure, TLedgerRole } from "@/zapi-frontend/shared/types/modelLedger";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";
import { ContextEmpty } from "../ProjectLayout/ui/ContextEmpty";
import { WidgetCard, WidgetCardHeader } from "../ProjectLayout/ui/WidgetCard";
import { useModelLedger } from "./useModelLedger";
import { buildModelPy, modelPyFileName } from "./ledgerExport";
import {
  LEDGER_ROLES,
  LEDGER_SOURCE_BUCKETS,
  dependentsOf,
  figureMatches,
  groupFigures,
  sourceBucket,
  type TLedgerSourceBucket
} from "./ledgerFormat";
import { ROLE_LABEL_KEY, SOURCE_LABEL_KEY } from "./ledgerLabels";
import { LedgerFilterPopover } from "./_LedgerFilterPopover";
import { LedgerColumns, LedgerGroup, LedgerSearch } from "./_LedgerParts";
import { ChecksPanel } from "./_ChecksPanel";
import { LedgerRow, type TSourceView } from "./_LedgerRowPanel";

const NARROW_BELOW = 560;

export function ModelLedgerIsland() {
  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const { t } = useTranslation("projectHome");
  const { data, isLoading } = useModelLedger(projectId);
  const projectName = useProjectStore((state) => state.selectedProject?.name ?? "");
  const setViewMode = useChapterStore((state) => state.setViewMode);
  const [searchParams, setSearchParams] = useSearchParams();
  const [listRef, listWidth] = useElementWidth();
  const narrow = listWidth > 0 && listWidth < NARROW_BELOW;
  const [query, setQuery] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<Set<TLedgerRole>>(() => new Set());
  const [selectedSources, setSelectedSources] = useState<Set<TLedgerSourceBucket>>(() => new Set());
  const deferredQuery = useDeferredValue(query);

  const figures = useMemo(() => data?.figures ?? [], [data]);
  const byKey = useMemo(() => new Map(figures.map((figure) => [figure.key, figure])), [figures]);
  const dependents = useMemo(() => dependentsOf(figures), [figures]);
  // A relation is a check the model makes, not a variable of the case, so the table leaves it out
  // and the checks panel above the table carries it instead.
  const variables = useMemo(() => figures.filter((figure) => figure.kind !== "relation"), [figures]);
  const relations = useMemo(() => figures.filter((figure) => figure.kind === "relation"), [figures]);
  const searched = useMemo(
    () => variables.filter((figure) => figureMatches(figure, deferredQuery)),
    [variables, deferredQuery]
  );

  const matchesRoles = useCallback(
    (figure: TLedgerFigure) => selectedRoles.size === 0 || (!!figure.role && selectedRoles.has(figure.role)),
    [selectedRoles]
  );
  const matchesSources = useCallback(
    (figure: TLedgerFigure) => selectedSources.size === 0 || selectedSources.has(sourceBucket(figure)),
    [selectedSources]
  );
  const groups = useMemo(
    () => groupFigures(searched.filter((figure) => matchesRoles(figure) && matchesSources(figure))),
    [searched, matchesRoles, matchesSources]
  );

  // Every role and source the ledger has, each counted over what the search and the other
  // filter leave, so a count is what the table would show once that option is ticked.
  const roleOptions = useMemo(
    () =>
      LEDGER_ROLES.filter((role) => variables.some((figure) => figure.role === role)).map((role) => ({
        value: role,
        label: t(ROLE_LABEL_KEY[role]),
        count: searched.filter((figure) => figure.role === role && matchesSources(figure)).length
      })),
    [variables, searched, matchesSources, t]
  );
  const sourceOptions = useMemo(
    () =>
      LEDGER_SOURCE_BUCKETS.filter((bucket) => variables.some((figure) => sourceBucket(figure) === bucket)).map(
        (bucket) => ({
          value: bucket,
          label: t(SOURCE_LABEL_KEY[bucket]),
          count: searched.filter((figure) => sourceBucket(figure) === bucket && matchesRoles(figure)).length
        })
      ),
    [variables, searched, matchesRoles, t]
  );

  const toggleIn = <V,>(set: Set<V>, value: V, checked: boolean) => {
    const next = new Set(set);
    if (checked) next.add(value);
    else next.delete(value);
    return next;
  };

  const [openKeys, setOpenKeys] = useState<Set<string>>(() => new Set());
  const [revealKey, setRevealKey] = useState<string | null>(null);
  const [highlightKey, setHighlightKey] = useState<string | null>(null);

  const toggleRow = useCallback((key: string) => {
    setOpenKeys((keys) => toggleIn(keys, key, !keys.has(key)));
  }, []);

  // A figure hidden by the search or a filter is shown again before it is opened, otherwise the
  // click would land on nothing.
  const revealRow = useCallback(
    (key: string) => {
      const figure = byKey.get(key);
      if (!figure) return;
      if (!figureMatches(figure, query) || !matchesRoles(figure) || !matchesSources(figure)) {
        setQuery("");
        setSelectedRoles(new Set());
        setSelectedSources(new Set());
      }
      setOpenKeys((keys) => new Set(keys).add(key));
      setRevealKey(key);
    },
    [byKey, query, matchesRoles, matchesSources]
  );

  // A figure card in a chapter links here with `?figure=<key>`: the row opens and scrolls into view
  // once the ledger has loaded, and the parameter goes so a reload does not jump again.
  // The parameter goes once the ledger has answered, known key or not, so a stale link does not
  // stick to the URL; an unknown key is left in the search box, so the reader sees what was asked
  // for and that the ledger has no such row.
  const figureParam = searchParams.get("figure");
  useEffect(() => {
    if (!figureParam || data === undefined) return;
    if (byKey.has(figureParam)) revealRow(figureParam);
    else setQuery(figureParam);
    setSearchParams(
      (params) => {
        params.delete("figure");
        return params;
      },
      { replace: true }
    );
    // Runs once the ledger has answered; `revealRow` changes with the search state it clears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [figureParam, data]);

  useEffect(() => {
    if (!revealKey) return;
    const row = document.querySelector<HTMLElement>(`[data-ledger-key="${CSS.escape(revealKey)}"]`);
    // The row is often not in the DOM on this pass: clearing the search also revealed it, and the
    // list is built from a deferred query, so this render is still the filtered one. Keeping the
    // key means the effect runs again when the list arrives, instead of scrolling to nothing once.
    if (!row) return;
    row.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlightKey(revealKey);
    setRevealKey(null);
  }, [revealKey, groups]);

  // Its own effect, because the one above clears `revealKey` and so tears down its own timer on
  // the very next render: the highlight was set and then never taken off again.
  useEffect(() => {
    if (!highlightKey) return;
    const timer = window.setTimeout(() => setHighlightKey(null), 1500);
    return () => window.clearTimeout(timer);
  }, [highlightKey]);

  const openView = useCallback(
    (view: TSourceView) => {
      setViewMode(view);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", view);
        params.delete("chapterId");
        params.delete("chapterSubsection");
        return params;
      });
    },
    [setViewMode, setSearchParams]
  );

  // A document source names the file the model cited, so the link downloads that file straight from
  // the Vault. When the Vault holds no file of that name, or the download fails, the link opens the
  // Vault instead, as it did before
  const openSource = useCallback(
    async (view: TSourceView, fileName?: string | null) => {
      if (!fileName) {
        openView(view);
        return;
      }
      const [response] = await tryCatch($api.get<{ data: TVaultFile[] }>(`/projects/${projectId}/documents`));
      const wanted = fileName.trim().split("/").pop()!.toLowerCase();
      const doc = response?.data.data.find((d) => d.fileName.trim().toLowerCase() === wanted);
      if (!doc || !(await downloadDocument(projectId, doc)).done) openView(view);
    },
    [openView, projectId]
  );

  // The model as the generation wrote and ran it; for a ledger written without one, the
  // same model rebuilt from the figures in the shape bread writes by hand.
  const downloadModelPy = () => {
    const source = data?.modelSource || buildModelPy(figures, projectName);
    const url = URL.createObjectURL(new Blob([source], { type: "text/x-python" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = modelPyFileName(projectName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const filterPopover = (
    <LedgerFilterPopover
      roles={roleOptions}
      sources={sourceOptions}
      selectedRoles={selectedRoles}
      selectedSources={selectedSources}
      onToggleRole={(role, checked) => setSelectedRoles((set) => toggleIn(set, role, checked))}
      onToggleSource={(bucket, checked) => setSelectedSources((set) => toggleIn(set, bucket, checked))}
      onReset={() => {
        setSelectedRoles(new Set());
        setSelectedSources(new Set());
      }}
    />
  );
  const moreMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full text-foreground"
            aria-label={t("modelLedger.more")}
            data-testid="model-ledger-more"
          />
        }
      >
        <DotsThreeIcon className="size-4" weight="bold" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-[220px] min-w-48 rounded-xl p-1.5">
        <DropdownMenuItem onClick={downloadModelPy} data-testid="model-ledger-download-py">
          <DownloadSimpleIcon className="size-4" />
          <span>{t("modelLedger.downloadModel")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
  const filtering = !!deferredQuery || selectedRoles.size > 0 || selectedSources.size > 0;
  const shown = groups.reduce((count, group) => count + group.rows.length, 0);

  return (
    <div className="relative flex w-full min-w-0 flex-col" data-testid="model-ledger-island">
      <div className="flex min-h-10 shrink-0 flex-wrap items-center justify-between gap-3">
        <h1 className="typo-h3 shrink-0 text-foreground">{t("modelLedger.title")}</h1>
        {data && (
          <div className="flex min-w-0 items-center gap-1">
            <LedgerSearch
              value={query}
              onChange={setQuery}
              placeholder={t("modelLedger.searchPlaceholder")}
              testId="model-ledger-search"
            >
              {filterPopover}
            </LedgerSearch>
            {moreMenu}
          </div>
        )}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : !data ? (
        <div className="pt-4">
          <ContextEmpty icon={SphereIcon} description={t("modelLedger.empty")} />
        </div>
      ) : (
        <div ref={listRef} data-testid="model-ledger-list">
          <div className="flex flex-col gap-4 pt-6 pb-8">
            {relations.length > 0 && !filtering && (
              <ChecksPanel relations={relations} byKey={byKey} narrow={narrow} onReveal={revealRow} />
            )}
            <WidgetCard>
              <WidgetCardHeader
                className="pe-5"
                title={t("rqaSim.variableCount", { count: variables.length })}
                actions={
                  filtering && (
                    <span className="typo-caption text-muted-foreground">
                      {t("rqaSim.inView", { shown, total: variables.length })}
                    </span>
                  )
                }
              />
              <LedgerColumns
                narrow={narrow}
                labels={[
                  t("modelLedger.columnVariable"),
                  t("modelLedger.columnSource"),
                  t("modelLedger.columnRole"),
                  t("modelLedger.columnValue")
                ]}
              />
              {groups.length === 0 ? (
                <p className="typo-body-sm px-5 py-10 text-center text-muted-foreground">
                  {t("modelLedger.noMatches")}
                </p>
              ) : (
                <div className="flex flex-col pb-2">
                  {groups.map((group) => (
                    <LedgerGroup key={group.name} name={group.name}>
                      {group.rows.map((figure) => (
                        <LedgerRow
                          key={figure.key}
                          figure={figure}
                          byKey={byKey}
                          dependents={dependents.get(figure.key) ?? []}
                          open={openKeys.has(figure.key)}
                          highlighted={highlightKey === figure.key}
                          narrow={narrow}
                          onToggle={toggleRow}
                          onReveal={revealRow}
                          onOpenView={openSource}
                        />
                      ))}
                    </LedgerGroup>
                  ))}
                </div>
              )}
            </WidgetCard>
          </div>
        </div>
      )}
    </div>
  );
}
